import { CalendarDays, Megaphone, MessageSquare, Plug } from 'lucide-react-native';
import type { ComponentType } from 'react';
import {
  BotIcon,
  ChartIcon,
  ChatIcon,
  GridIcon,
  PhoneIcon,
  PipelineIcon,
  SettingsIcon,
  UsersIcon,
  WorkflowsIcon,
} from '../components/icons/commandDeckIcons';
import { isCampaignsEnabled, isLeadSourcesEnabled, isWhatsAppEnabled } from '../lib/featureFlags';

export interface NavItem {
  labelKey: string;
  href: string;
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  badge?: string;
}

export interface NavSection {
  titleKey: string;
  items: NavItem[];
}

const LEAD_SOURCES_HREF = '/lead-sources';

const GATED_HREFS: ReadonlyArray<{ href: string; isEnabled: () => boolean }> = [
  { href: LEAD_SOURCES_HREF, isEnabled: isLeadSourcesEnabled },
  { href: '/campaigns', isEnabled: isCampaignsEnabled },
  { href: '/whatsapp', isEnabled: isWhatsAppEnabled },
];

function withoutGatedItems(items: NavItem[]): NavItem[] {
  const hiddenHrefs = GATED_HREFS.filter((gate) => !gate.isEnabled()).map((gate) => gate.href);
  return items.filter((item) => !hiddenHrefs.includes(item.href));
}

export const mainNav: NavItem[] = withoutGatedItems([
  { labelKey: 'layout.nav.dashboard', href: '/dashboard', icon: GridIcon },
  { labelKey: 'layout.nav.pipeline', href: '/funnel', icon: PipelineIcon },
  { labelKey: 'layout.nav.leads', href: '/leads', icon: UsersIcon },
  { labelKey: 'layout.nav.calendar', href: '/calendar', icon: CalendarDays },
  { labelKey: 'layout.nav.analytics', href: '/analytics', icon: ChartIcon },
  { labelKey: 'layout.nav.chat', href: '/chat', icon: ChatIcon },
  { labelKey: 'layout.nav.aiAgents', href: '/ai-agent', icon: BotIcon },
  { labelKey: 'layout.nav.workflows', href: '/workflows', icon: WorkflowsIcon },
  { labelKey: 'layout.nav.voiceAgents', href: '/voice-agents', icon: PhoneIcon },
  { labelKey: 'layout.nav.campaigns', href: '/campaigns', icon: Megaphone },
  { labelKey: 'layout.nav.whatsapp', href: '/whatsapp', icon: MessageSquare },
  { labelKey: 'layout.nav.leadSources', href: LEAD_SOURCES_HREF, icon: Plug },
]);

export const accountNav: NavItem[] = [
  { labelKey: 'layout.nav.settings', href: '/settings', icon: SettingsIcon },
];

// Maps a top-level nav href to the route name of its tab inside the (app)
// Tabs navigator. Tabs that own detail routes are route groups, so their tab
// route name carries the parentheses ((leads), (workflows)); leaf tabs use
// their bare segment. Used to perform a state-preserving tab jump (focus the
// tab without resetting its nested stack). Hrefs not listed here aren't (app)
// tabs (e.g. /privacy-policy) and fall back to normal router navigation.
export const TAB_ROUTE_BY_HREF: Readonly<Record<string, string>> = {
  '/dashboard': 'dashboard',
  '/funnel': 'funnel',
  '/leads': '(leads)',
  '/calendar': 'calendar',
  '/analytics': 'analytics',
  '/chat': 'chat',
  '/ai-agent': 'ai-agent',
  '/workflows': '(workflows)',
  '/voice-agents': 'voice-agents',
  '/campaigns': 'campaigns',
  '/whatsapp': 'whatsapp',
  '/settings': 'settings',
  '/mission-control': 'mission-control',
  '/lead-sources': '(lead-sources)',
};

// Detail / sub routes that visually belong to a top-level nav destination but
// don't share its href prefix. Each entry maps a route prefix to the nav href
// that should read as "active" while the user is on that route. The /lead/
// prefix intentionally keeps its trailing slash: '/leads'.startsWith('/lead')
// is true, so without the slash the Leads tab itself would be misrouted.
const DETAIL_ROUTE_PARENTS: ReadonlyArray<{ prefix: string; parent: string }> = [
  { prefix: '/lead/', parent: '/leads' },
  { prefix: '/linkedin-search', parent: '/leads' },
];

const KNOWN_NAV_HREFS: readonly string[] = [...mainNav, ...accountNav].map((item) => item.href);

const LABEL_KEY_BY_HREF: Readonly<Record<string, string>> = Object.fromEntries(
  [...mainNav, ...accountNav].map((item) => [item.href, item.labelKey]),
);

// Resolves the translation key for a tab's title given its (app) Tabs route
// name (e.g. '(leads)' -> 'layout.nav.leads'). Lets chrome that reads the
// focused tab — rather than the pathname, which can be stale after a
// state-preserving tab jump — show the correct title. Returns null when the
// route name isn't a known tab.
export function getTabLabelKey(tabRouteName: string): string | null {
  const href = Object.keys(TAB_ROUTE_BY_HREF).find((h) => TAB_ROUTE_BY_HREF[h] === tabRouteName);
  return href ? (LABEL_KEY_BY_HREF[href] ?? null) : null;
}

// Resolves which top-level nav destination "owns" a given pathname so the
// sidebar and mobile tab bar can highlight the correct item on detail screens
// (e.g. a lead detail at /lead/:id should keep the Leads tab active, not fall
// back to Dashboard). Returns null for routes that map to no known nav item,
// letting callers apply their own fallback.
export function resolveActiveNavHref(pathname: string): string | null {
  for (const { prefix, parent } of DETAIL_ROUTE_PARENTS) {
    if (pathname === prefix || pathname.startsWith(prefix)) return parent;
  }

  // Match the longest known href so nested routes like /voice-agents/calls/:id
  // resolve to /voice-agents rather than an accidental shorter prefix.
  let best: string | null = null;
  for (const href of KNOWN_NAV_HREFS) {
    if (pathname === href || pathname.startsWith(`${href}/`)) {
      if (!best || href.length > best.length) best = href;
    }
  }
  return best;
}

// Reverse of TAB_ROUTE_BY_HREF: maps a focused tab route name (e.g. '(leads)')
// back to its nav href ('/leads'). Returns null when the name isn't a known tab.
export function getNavHrefForTabRoute(tabRouteName: string): string | null {
  return Object.keys(TAB_ROUTE_BY_HREF).find((h) => TAB_ROUTE_BY_HREF[h] === tabRouteName) ?? null;
}

// Active nav href for the navigation chrome (sidebar / mobile tab bar). Prefers
// the Tabs navigator's focused tab — a state-preserving tab jump doesn't always
// refresh the pathname, so reading the pathname alone lagged the active
// highlight by one click. Falls back to the pathname mapping when the Tabs
// navigation isn't captured yet or the route isn't an (app) tab.
export function resolveChromeActiveNavHref(
  activeTabRouteName: string | null,
  pathname: string,
): string | null {
  if (activeTabRouteName) {
    const hrefForTab = getNavHrefForTabRoute(activeTabRouteName);
    if (hrefForTab) return hrefForTab;
  }
  return resolveActiveNavHref(pathname);
}

const allNavSections: NavSection[] = [
  {
    titleKey: 'layout.navSections.main',
    items: [
      { labelKey: 'layout.nav.dashboard', href: '/dashboard', icon: GridIcon },
      { labelKey: 'layout.nav.pipeline', href: '/funnel', icon: PipelineIcon },
      { labelKey: 'layout.nav.leads', href: '/leads', icon: UsersIcon },
      { labelKey: 'layout.nav.calendar', href: '/calendar', icon: CalendarDays },
    ],
  },
  {
    titleKey: 'layout.navSections.insights',
    items: [{ labelKey: 'layout.nav.analytics', href: '/analytics', icon: ChartIcon }],
  },
  {
    titleKey: 'layout.navSections.automation',
    items: [
      { labelKey: 'layout.nav.chat', href: '/chat', icon: ChatIcon },
      { labelKey: 'layout.nav.aiAgents', href: '/ai-agent', icon: BotIcon },
      { labelKey: 'layout.nav.workflows', href: '/workflows', icon: WorkflowsIcon },
      { labelKey: 'layout.nav.voiceAgents', href: '/voice-agents', icon: PhoneIcon },
      { labelKey: 'layout.nav.campaigns', href: '/campaigns', icon: Megaphone },
      { labelKey: 'layout.nav.whatsapp', href: '/whatsapp', icon: MessageSquare },
      { labelKey: 'layout.nav.leadSources', href: LEAD_SOURCES_HREF, icon: Plug },
    ],
  },
];

export const navSections: NavSection[] = allNavSections.map((section) => ({
  ...section,
  items: withoutGatedItems(section.items),
}));
